import { IList } from 'annotation-tools/utils';
import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import { tooltipMessages } from '../../constants/messages/tooltip-messages';
import { IdType, ILabelType } from '../../imports/annotation-canvas';
import { EditButtons, NavigationButtons } from '../../imports/ui-parts';
import { ButtonStateType, Percent } from '../../imports/utils';
import { AppMode } from '../../types/enum/app-mode';
import { ToolbarButtonIdEnum } from '../../types/enum/toolbar-button-id-enum';
import { ModeButtons } from './mode-buttons/mode-buttons';
import { OtherButtons } from './other-buttons';

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  flex-wrap: wrap;
  padding: 5px;
`;

const ActionControlLeft = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: auto;
`;

const ActionControlCenter = styled.div`
  width: 420px;
`;

const ActionControlRight = styled.div`
  width: auto;
`;

export const Toolbar = memo(
  ({
    currentPageIndex,
    pageLength,
    appMode,
    labels,
    labelSaturation,
    labelLightness,
    onAppModeChange,
    onLabelClick,
    onToolbarButtonClick,
    onPageChange,
    editButtonsDisabled,
    navButtonsIsDisabled,
    inferButtonState,
    openSettingsDialogButtonState
  }: Readonly<{
    currentPageIndex: number;
    pageLength: number;
    appMode: AppMode;
    labels: IList<ILabelType>;
    labelSaturation: Percent;
    labelLightness: Percent;
    onAppModeChange: (mode: AppMode) => void;
    onLabelClick: (id: IdType) => void;
    onToolbarButtonClick: (id: ToolbarButtonIdEnum) => void;
    onPageChange: (pageIndex: number) => void;
    editButtonsDisabled: boolean;
    navButtonsIsDisabled: {
      firstPage: boolean;
      prevPage: boolean;
      nextPage: boolean;
      lastPage: boolean;
    };
    inferButtonState: ButtonStateType;
    openSettingsDialogButtonState: ButtonStateType;
  }>) => {
    const {
      onBackToListClick,
      onHelpClick,
      onInferClick,
      onSettingsDialogClick,
      onUndoClick,
      onRedoClick,
      onRemoveSelectedClick,
      onRemoveAllClick
    } = useMemo(
      () => ({
        onBackToListClick: () => onToolbarButtonClick('back-to-list'),
        onHelpClick: () => onToolbarButtonClick('help'),
        onInferClick: () => onToolbarButtonClick('infer'),
        onSettingsDialogClick: () => onToolbarButtonClick('settings'),
        onUndoClick: () => onToolbarButtonClick('undo'),
        onRedoClick: () => onToolbarButtonClick('redo'),
        onRemoveSelectedClick: () => onToolbarButtonClick('remove-selected'),
        onRemoveAllClick: () => onToolbarButtonClick('remove-all')
      }),
      [onToolbarButtonClick]
    );

    return (
      <Root>
        <ActionControlLeft>
          <OtherButtons
            inferButtonState={inferButtonState}
            onInferClick={onInferClick}
            openSettingsDialogButtonState={openSettingsDialogButtonState}
            onSettingsDialogClick={onSettingsDialogClick}
            onBackToListClick={onBackToListClick}
            onHelpClick={onHelpClick}
          />
          <ModeButtons
            tooltipMessages={tooltipMessages.appMode}
            labels={labels}
            labelSaturation={labelSaturation}
            labelLightness={labelLightness}
            appModeSelected={appMode}
            onLabelClick={onLabelClick}
            onAppModeChange={onAppModeChange}
          />
        </ActionControlLeft>
        <ActionControlCenter>
          <NavigationButtons
            tooltipMessages={tooltipMessages.navigation}
            currentPageIndex={currentPageIndex}
            pageLength={pageLength}
            isDisabled={navButtonsIsDisabled}
            onPageChange={onPageChange}
          />
        </ActionControlCenter>
        <ActionControlRight>
          <EditButtons
            tooltipMessages={tooltipMessages.toolbar}
            onUndoClick={onUndoClick}
            onRedoClick={onRedoClick}
            onRemoveSelectedClick={onRemoveSelectedClick}
            onRemoveAllClick={onRemoveAllClick}
            disabled={editButtonsDisabled}
          />
        </ActionControlRight>
      </Root>
    );
  }
);

Toolbar.displayName = 'Toolbar';
